import React, { useEffect, useRef, useState, createContext, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import api, { WS_URL, resolveUrl } from '../utils/api';
import CallOverlay from './CallOverlay';
import AdBanner from './AdBanner';

import { useWebRTC } from '../hooks/useWebRTC';
import { getTheme } from '../utils/theme';

const theme = getTheme();

export const NotificationContext = createContext({});
export const CallContext = createContext({});
export const ConfirmContext = createContext({});

const MsgIcon  = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const RandIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>;
const FrndIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const FeedIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
const MenuIcon = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
const CloseIcon= () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const LogoutIcon=() => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
const FeedbackIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>;
const PrivacyIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;


// Generic confirmation modal for all destructive actions
function ConfirmModal({ title, message, icon, confirmText, onConfirm, onCancel, confirmColor = '#ef4444' }) {
  return (
    <div style={S.modalOverlay}>
      <div style={S.modalBox}>
        <div style={S.modalIcon}>{icon || '❓'}</div>
        <h3 style={S.modalTitle}>{title || 'Are you sure?'}</h3>
        <p style={S.modalSub}>{message || 'This action cannot be undone.'}</p>
        <div style={S.modalBtns}>
          <button style={S.modalCancel} onClick={onCancel}>Cancel</button>
          <button style={{ ...S.modalConfirm, background: confirmColor }} onClick={onConfirm}>{confirmText || 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCounts, setUnreadCounts] = useState({ chat: 0, random: 0, friends: 0, feed: 0 });
  const [notifications, setNotifications] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState(null); // { title, message, icon, confirmText, onConfirm, confirmColor }
  const [callPartnerName, setCallPartnerName] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const globalWsRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // FIX: always read latest values from localStorage (they get updated by Profile page)
  const [username, setUsername] = useState(localStorage.getItem('username') || '');
  const [userAvatar, setUserAvatar] = useState(localStorage.getItem('userAvatar') || '');
  const isGuest = localStorage.getItem('isGuest') === 'true';
  const isStaff = localStorage.getItem('is_staff') === 'true';
  const isSuperuser = localStorage.getItem('is_superuser') === 'true';
  const isAdmin = isStaff || isSuperuser;

  // Refresh avatar/username when navigating (in case Profile updated them)
  useEffect(() => {
    setUsername(localStorage.getItem('username') || '');
    setUserAvatar(localStorage.getItem('userAvatar') || '');
  }, [location.pathname]);

  const rtc = useWebRTC(globalWsRef, null);
  const rtcRef = useRef(rtc);
  useEffect(() => { rtcRef.current = rtc; }, [rtc]);

  useEffect(() => {
    if (location.pathname === '/feed' && unreadCounts.feed > 0) {
      setUnreadCounts(prev => ({ ...prev, feed: 0 }));
      api.post('/reset-feed-count/').catch(() => {});
    }
  }, [location.pathname, unreadCounts.feed]);

  const showNotification = useCallback((msg, duration = 4000) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, msg }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 4000);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let ws;
    const connect = () => {
      ws = new WebSocket(`${WS_URL}/ws/notifications/?token=${token}`);
      globalWsRef.current = ws;
      ws.onmessage = async (e) => {
        const data = JSON.parse(e.data);
        if (data.type === 'unread_counts') {
          setUnreadCounts({
            chat: data.chat_unread_count || 0,
            random: data.random_chat_unread_count || 0,
            friends: data.friend_requests_count || 0,
            // FIX: cap feed at 10+
            feed: Math.min(data.new_posts_count || 0, 99),
          });
        } else if (data.type === 'friend_request_notification') {
          // Only show toast for new incoming requests, not acceptance notifications
          if (!data.subtype || data.subtype === 'new') {
            showNotification(`${data.sender_username} sent you a friend request`);
          } else if (data.subtype === 'accepted') {
            showNotification(`${data.sender_username} accepted your friend request!`);
          }
          setUnreadCounts(prev => ({ ...prev, friends: prev.friends + 1 }));
        } else if (['call_request','call_accept','call_reject','call_end','webrtc_offer','webrtc_answer','webrtc_ice_candidate'].includes(data.type)) {
          if (data.type === 'call_request') {
            setCallPartnerName(data.sender_username || 'User');
            if (ringRef.current) {
              ringRef.current.loop = true;
              ringRef.current.currentTime = 0;
              ringRef.current.play().catch(() => {});
            }
          }
          await rtcRef.current.handleSignal(data);
        }
      };
      ws.onclose = () => { setTimeout(() => { if (localStorage.getItem('token')) connect(); }, 3000); };
    };
    connect();
    return () => { if (ws) ws.close(); };
  }, []);

  useEffect(() => {
    if (rtc.callState !== 'receiving' && ringRef.current) { ringRef.current.pause(); ringRef.current.currentTime = 0; }
  }, [rtc.callState]);

  const confirm = useCallback((cfg) => {
    return new Promise((resolve) => {
      setConfirmConfig({
        ...cfg,
        onConfirm: () => { setConfirmConfig(null); resolve(true); },
        onCancel: () => { setConfirmConfig(null); resolve(false); }
      });
    });
  }, []);

  const handleLogout = async () => {
    try { await api.post('/logout/'); } catch {}
    localStorage.clear();
    // FIX: replace:true prevents back-button returning to protected pages
    navigate('/login', { replace: true });
  };

  const navItems = [
    { to: '/chat',    Icon: MsgIcon,  label: 'Messages', badge: unreadCounts.chat },
    { to: '/random',  Icon: RandIcon, label: 'Random',   badge: unreadCounts.random },
    { to: '/friends', Icon: FrndIcon, label: 'Friends',  badge: unreadCounts.friends },
    { to: '/feed',    Icon: FeedIcon, label: 'Feed',     badge: unreadCounts.feed },
    { to: '/privacy', Icon: PrivacyIcon, label: 'Privacy', badge: 0 },
    { to: '/feedback',Icon: FeedbackIcon, label: 'Feedback',badge: 0 },

  ];

  const domainName = window.location.hostname.includes('lloop.in') ? 'LLOOP' : window.location.hostname.includes('robloxs.in') ? 'ROBLOXS' : theme.brandName;
  const pageTitle = domainName;

  // Badge display: cap feed at 10+, others at 99+
  const badgeLabel = (badge, isFeed) => {
    if (isFeed) return badge > 10 ? '10+' : badge;
    return badge > 99 ? '99+' : badge;
  };

  return (
    <NotificationContext.Provider value={{ showNotification, unreadCounts }}>
      <CallContext.Provider value={{ rtc, wsRef: globalWsRef }}>
        <ConfirmContext.Provider value={{ confirm }}>
        {/* use --dvh for real viewport height, prevents keyboard bounce on mobile */}
        <div style={{ ...S.root, height: 'calc(var(--dvh, 1vh) * 100)' }}>
          <audio ref={ringRef} loop preload="auto">
            <source src="/ringtone.mp3" type="audio/mpeg" />
            <source src="/ringtone.wav" type="audio/wav" />
          </audio>

          {confirmConfig && (
            <ConfirmModal
              {...confirmConfig}
            />
          )}

          {showLogoutModal && (
            <ConfirmModal
              title="Log out?"
              message={`Are you sure you want to log out of ${theme.brandName}?`}
              icon="🚪"
              confirmText="Log Out"
              onConfirm={handleLogout}
              onCancel={() => setShowLogoutModal(false)}
            />
          )}

          {sidebarOpen && <div style={S.overlay} onClick={() => setSidebarOpen(false)} />}

          <aside className={`app-sidebar${sidebarOpen ? ' open' : ''}`} style={S.sidebar}>
            <div style={S.sidebarTop}>
              <span style={S.logoText}>
                {theme.brandLogo.startsWith('/') ? <img src={theme.brandLogo} alt={theme.brandName} style={{ height: 28 }} /> : theme.brandLogo}
              </span>
              <button className="sidebar-close-btn" style={S.iconBtn} onClick={() => setSidebarOpen(false)}><CloseIcon /></button>
            </div>

            <nav style={S.nav}>
              {navItems.map(({ to, Icon, label, badge }) => (
                <NavLink key={to} to={to} onClick={() => setSidebarOpen(false)}
                  style={({ isActive }) => ({ ...S.navLink, ...(isActive ? S.activeLink : {}) })}>
                  <span style={S.navIcon}><Icon /></span>
                  <span style={S.navLabel}>{label}</span>
                  {badge > 0 && <span style={S.badge}>{badgeLabel(badge, to === '/feed')}</span>}
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink to="/admin-panel" onClick={() => setSidebarOpen(false)}
                  style={{ ...S.navLink, background: 'rgba(99,102,241,0.08)' }}>
                  <span style={S.navIcon}>⚡</span>
                  <span style={S.navLabel}>Admin Panel</span>
                  <span style={{ fontSize: 10, color: '#818cf8', fontWeight: 700, background: 'rgba(99,102,241,0.15)', padding: '2px 7px', borderRadius: 20 }}>ADMIN</span>
                </NavLink>
              )}
            </nav>
            <div style={S.userSection}>
              <button style={S.avatarBtn} onClick={() => { navigate('/profile'); setSidebarOpen(false); }}>
                {userAvatar
                  ? <img src={resolveUrl(userAvatar)} alt="me" style={S.avatarImg} onError={e => e.target.style.display='none'} />
                  : <div style={S.avatarInit}>{username?.[0]?.toUpperCase()}</div>}
              </button>
              <div style={S.userInfo}>
                <span style={S.usernameText}>{username}</span>
                {isGuest && <span style={S.guestBadge}>Guest</span>}
              </div>
              <button onClick={() => setShowLogoutModal(true)} style={S.iconBtn} title="Logout"><LogoutIcon /></button>
            </div>
          </aside>

          <div style={{ ...S.mainWrap, marginLeft: windowWidth >= 1024 ? 240 : 0 }}>
            {(['/chat', '/random', '/friends', '/feed', '/feedback', '/privacy'].some(p => location.pathname === p || (p === '/chat' && location.pathname.startsWith('/chat/')))) && (

              <AdBanner />
            )}
            <header style={S.topbar}>
              <button style={S.iconBtn} onClick={() => setSidebarOpen(true)}><MenuIcon /></button>
              <span style={S.topbarTitle}>
                {theme.brandLogo.startsWith('/') ? <img src={theme.brandLogo} alt={theme.brandName} style={{ height: 20 }} /> : pageTitle}
              </span>

              <div style={S.topbarNav}>
                {navItems.map(({ to, Icon, badge }) => (
                  <NavLink key={to} to={to}
                    style={({ isActive }) => ({ ...S.topNavBtn, ...(isActive ? S.topNavActive : {}) })}>
                    <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Icon />
                      {badge > 0 && <span style={S.topBadge}>{badgeLabel(badge, to === '/feed')}</span>}
                    </span>
                  </NavLink>
                ))}
              </div>
              <button style={{ ...S.avatarBtn, marginLeft: 4 }} onClick={() => navigate('/profile')}>
                {userAvatar
                  ? <img src={resolveUrl(userAvatar)} alt="me" style={S.avatarImgSm} onError={e => e.target.style.display='none'} />
                  : <div style={S.avatarSm}>{username?.[0]?.toUpperCase()}</div>}
              </button>
            </header>

            <main style={S.main}>
              <Outlet />
            </main>
          </div>

          <div style={S.toastWrap}>
            {notifications.map(n => <div key={n.id} style={S.toast}>{n.msg}</div>)}
          </div>

          <CallOverlay
            callState={rtc.callState} callType={rtc.callType}
            callerName={callPartnerName || rtc.callerName}
            isMuted={rtc.isMuted} isVideoOff={rtc.isVideoOff}
            localVideoRef={rtc.localVideoRef} remoteVideoRef={rtc.remoteVideoRef}
            onAccept={rtc.acceptPendingCall} onReject={rtc.rejectCall} onEnd={rtc.endCall}
            onToggleMute={rtc.toggleMute} onToggleVideo={rtc.toggleVideo}
          />
        </div>
        </ConfirmContext.Provider>
      </CallContext.Provider>
    </NotificationContext.Provider>
  );
}

const S = {
  root: { display:'flex', flexDirection:'row', background:'#0a0a0f', color:'#e2e8f0', fontFamily:"'Inter',system-ui,sans-serif", overflow:'hidden', position:'relative', width:'100%' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:199 },
  sidebar: {
    /* Base styles — CSS classes in index.html override per breakpoint */
    position:'fixed', left:'-260px', top:0, bottom:0, width:240,
    background:'#111118', borderRight:`1px solid rgba(${theme.colors.primaryRGB},0.15)`,
    display:'flex', flexDirection:'column', zIndex:200,
    transition:'left 0.25s ease',
  },
  sidebarTop: { padding:'20px 16px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid rgba(255,255,255,0.06)' },
  logoText: { fontSize: 20, fontWeight:800, background:theme.colors.gradient, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' },
  iconBtn: { background:'none', border:'none', color:'#64748b', cursor:'pointer', padding:6, display:'flex', alignItems:'center', borderRadius:8 },
  nav: { flex:1, display:'flex', flexDirection:'column', gap:2, padding:'10px 8px' },
  navLink: { display:'flex', alignItems:'center', gap:12, padding:'11px 12px', borderRadius:10, textDecoration:'none', color:'#64748b', fontSize: 12, fontWeight:500, position:'relative', transition:'all 0.15s' },
  activeLink: { background:`rgba(${theme.colors.primaryRGB},0.15)`, color:theme.colors.primary },
  navIcon: { display:'flex', alignItems:'center', flexShrink:0 },
  navLabel: { flex:1 },
  badge: { background:'#ef4444', color:'#fff', borderRadius:999, padding:'1px 6px', fontSize: 10, fontWeight:700 },
  userSection: { display:'flex', alignItems:'center', gap:10, padding:'12px 14px', borderTop:'1px solid rgba(255,255,255,0.06)' },
  avatarBtn: { background:'none', border:'none', cursor:'pointer', padding:0, flexShrink:0 },
  avatarImg: { width:34, height:34, borderRadius:'50%', objectFit:'cover', border:`2px solid rgba(${theme.colors.primaryRGB},0.4)` },
  avatarInit: { width:34, height:34, borderRadius:'50%', background:theme.colors.gradient, display:'flex', alignItems:'center', justifyContent:'center', fontSize: 12, fontWeight:700, color:'#fff' },
  userInfo: { flex:1, overflow:'hidden' },
  usernameText: { fontSize: 11, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block' },
  guestBadge: { fontSize:10, color:'#f59e0b', background:'rgba(245,158,11,0.15)', borderRadius:4, padding:'1px 5px' },
  mainWrap: { flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0, width:'100%', minHeight:0 },
  topbar: { display:'flex', alignItems:'center', padding:'8px 10px', background:'#111118', borderBottom:`1px solid rgba(${theme.colors.primaryRGB},0.15)`, gap:4, flexShrink:0 },
  topbarTitle: { fontSize: 14, fontWeight:800, background:theme.colors.gradient, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', flex:1, marginLeft:4 },
  topbarNav: { display:'flex', alignItems:'center', gap:2 },
  topNavBtn: { display:'flex', alignItems:'center', justifyContent:'center', padding:7, borderRadius:8, color:'#475569', textDecoration:'none', transition:'all 0.15s' },
  topNavActive: { color:theme.colors.primaryHover, background:`rgba(${theme.colors.primaryRGB},0.12)` },
  topBadge: { position:'absolute', top:-5, right:-5, background:'#ef4444', color:'#fff', borderRadius:999, padding:'0 3px', fontSize:9, fontWeight:700, minWidth:14, height:14, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 },
  avatarImgSm: { width:30, height:30, borderRadius:'50%', objectFit:'cover', border:`2px solid rgba(${theme.colors.primaryRGB},0.4)` },
  avatarSm: { width:30, height:30, borderRadius:'50%', background:theme.colors.gradient, display:'flex', alignItems:'center', justifyContent:'center', fontSize: 10, fontWeight:700, color:'#fff' },
  main: { flex:1, overflow:'hidden', display:'flex', flexDirection:'column', minHeight:0 },
  toastWrap: { position:'fixed', top:16, right:16, zIndex:9999, display:'flex', flexDirection:'column', gap:8, pointerEvents:'none' },
  toast: { background:'#1a1a2e', border:`1px solid rgba(${theme.colors.primaryRGB},0.4)`, color:'#e2e8f0', padding:'10px 16px', borderRadius:10, fontSize: 11, boxShadow:'0 8px 24px rgba(0,0,0,0.5)', maxWidth:300, animation:'toastIn 0.3s ease' },
  // Logout modal
  modalOverlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 },
  modalBox: { background:'#111118', border:`1px solid rgba(${theme.colors.primaryRGB},0.25)`, borderRadius:20, padding:'32px 28px', width:'100%', maxWidth:360, textAlign:'center', boxShadow:'0 24px 64px rgba(0,0,0,0.6)' },
  modalIcon: { fontSize: 38, marginBottom:12 },
  modalTitle: { color:'#e2e8f0', fontSize: 18, fontWeight:700, margin:'0 0 8px' },
  modalSub: { color:'#64748b', fontSize: 12, margin:'0 0 24px' },
  modalBtns: { display:'flex', gap:12 },
  modalCancel: { flex:1, padding:'12px', borderRadius:10, border:'1px solid rgba(255,255,255,0.1)', background:'none', color:'#94a3b8', fontSize: 12, fontWeight:600, cursor:'pointer' },
  modalConfirm: { flex:1, padding:'12px', borderRadius:10, border:'none', background:'#ef4444', color:'#fff', fontSize: 12, fontWeight:700, cursor:'pointer' },
};
